/**
 * サーバーの鍵（sk_）のアカウント単位の画面（2026-09-28 creator/dashboard-pass・Core SEC-PKLOGIN-01 の続き・Kumo 管理の判断 (b)）。
 *
 *   /account/secret-keys         全プロジェクトの有効な sk_ の一覧（各プロジェクトの API キーの画面へのリンク）。通知メール
 *                                「サーバーの鍵をまとめて作り直しました」のボタンの行き先
 *   /account/secret-keys/rotate  まとめて作り直す。再設定の完了の画面・通知メール「パスワードが変わりました」のボタンの行き先
 *
 * API の正（Core・読むだけ）: apps/server/src/modules/platform/secretKeys.ts。判断・形の読み取りは ../secretKeys.ts（テストはそちら）。
 *   一覧も preview（GET …/rotate-all/preview）を使う（指示書: 一覧のための別の API は無い。preview は何も変えない）。
 *
 * まとめて作り直すの流れ:
 *   1. preview の一覧を見せる（Functions の自動の鍵の行は「Functions を再デプロイ」）。見せた一覧の version を持つ
 *   2. 押す → POST …/rotate-all { version }（証明なし）→ 400 reauthentication_required → 共通の再認証（components/Reauth.tsx）→
 *      { version, password | reauthToken } で送り直す。サーバーは version の照合を再認証より先にする = 409 で kdra_ を燃やさない
 *   3. 成功 → 新しい鍵を 1 度だけ出す（プロジェクト・環境ごと・コピー・「離れると二度と見られません」）。
 *      functionsRedeployRequired には `functions deploy --env …` の案内
 *   409 keys_changed → preview を取り直して見せ直す（自動で実行し直さない = 見せた一覧と失効する一覧をずらさない）
 *   409 no_secret_keys → 取り直す（空の表示）/ 403 email_unverified → 確認メールの案内
 *   「Google で確認」はページを移る → 戻ったら一覧を読み直し、「確認できました。もう一度押して」（自動では送らない: 取り消せない操作の最後の 1 回は本人が押す
 *    = AccountPage の閉鎖と同じ・Reauth.tsx の WHY）。押したときに run が戻りの証明（pending）を使う
 *
 * 新しい鍵の値は、このコンポーネントの state（メモリ）にだけ置く。localStorage・URL・history.state には置かない（サーバーは sha256 しか持たない）。
 * タブを閉じる・再読み込みは beforeunload で一度止める。画面の中のリンクで離れる場合は止めない（BrowserRouter では遷移を止める口が無い）ので、
 *   値の横に「離れると二度と見られません」を目立たせて出す。
 * kdt_（MCP の委任）はこの操作を使えない（サーバーが 403）。ダッシュボードは kds_ なので関係しない。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, keyHead, type ApiError } from '../api';
import { CLI } from '../cliCommand';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { useReauth, useReauthFailureText } from '../components/Reauth';
import { SecretKeyTable } from '../components/SecretKeyTable';
import { CodeBlock, CopyButton, Empty, ErrorState, PageHeader, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, useT } from '../i18n';
import {
  functionsDeployCommand,
  groupByProject,
  readPreview,
  readRotateResult,
  ROTATE_PATH,
  rotateFailure,
  SECRET_KEYS_PATH,
  type RotateResult
} from '../secretKeys';
import { TopBar } from './ProjectsPage';

const PREVIEW = '/v1/developers/me/secret-keys/rotate-all/preview';
/** 再認証の戻り（「Google で確認」）で続ける操作の名前（Reauth.tsx の action） */
const ROTATE_ACTION = 'secret-keys:rotate-all';

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="page-wrap">
      <TopBar />
      <main className="container narrow" id="main">
        {children}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------- 一覧

export function SecretKeysPage() {
  const { t } = useT();
  const preview = useResource<unknown>(PREVIEW);
  const keys = preview.data ? readPreview(preview.data).keys : [];
  return (
    <Frame>
      <PageHeader eyebrow={t('skList.eyebrow')} title={t('skList.title')}>
        {keys.length > 0 && (
          <Link to={ROTATE_PATH} className="btn btn-danger">
            {t('skList.rotateAll')}
          </Link>
        )}
      </PageHeader>
      <p className="muted page-lead">{t('skList.lead')}</p>
      {preview.loading && !preview.data && <Spinner />}
      {preview.error && <ErrorState error={preview.error} onRetry={preview.reload} />}
      {!!preview.data && keys.length === 0 && <Empty title={t('skList.empty')} icon="lock" />}
      {keys.length > 0 && (
        <section className="card">
          <p className="small muted">{t('skList.rotateHint')}</p>
          <SecretKeyTable keys={keys} linkToKeys />
        </section>
      )}
    </Frame>
  );
}

// ---------------------------------------------------------------- まとめて作り直す

export function RotateSecretKeysPage() {
  const { t } = useT();
  const reauth = useReauth();
  const failureText = useReauthFailureText();
  const preview = useResource<unknown>(PREVIEW);
  const [result, setResult] = useState<RotateResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<'changed' | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 「Google で確認」から戻ってきた（この操作の途中だった）。最後の 1 回は本人に押してもらう（冒頭の WHY）
  const resumed = reauth.pending?.action === ROTATE_ACTION ? reauth.pending : null;
  const [resumeFailure] = useState(() => (reauth.failure?.action === ROTATE_ACTION ? reauth.failure : null));
  const { consume } = reauth;
  useEffect(() => {
    if (resumeFailure) consume();
  }, [resumeFailure, consume]);

  const data = preview.data ? readPreview(preview.data) : null;
  const keys = data?.keys ?? [];

  const rotate = async () => {
    if (!data || !data.version || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    setUnverified(false);
    try {
      // 見せている一覧の version を送る（違えば 409 keys_changed）。証明は run が足す（戻りの証明があればそれ・無ければ断られてからダイアログ）
      const r = await reauth.run((proof) => api<unknown>('/v1/developers/me/secret-keys/rotate-all', { method: 'POST', body: { version: data.version, ...proof } }), {
        action: ROTATE_ACTION
      });
      if (r === null) return; // 確認を取り消した = 何も変わっていない
      setResult(readRotateResult(r));
    } catch (err) {
      const e = err as ApiError;
      const kind = rotateFailure(e);
      if (kind === 'keys_changed' || kind === 'no_secret_keys') {
        if (kind === 'keys_changed') setNotice('changed');
        preview.reload();
      } else if (kind === 'email_unverified') setUnverified(true);
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (result) return <RotateDone result={result} />;

  return (
    <Frame>
      <PageHeader eyebrow={t('skList.title')} title={t('rotate.title')} />
      <p className="page-lead">{t('rotate.lead')}</p>
      {reauth.dialog}
      {preview.loading && !preview.data && <Spinner />}
      {preview.error && <ErrorState error={preview.error} onRetry={preview.reload} />}
      {notice === 'changed' && (
        <p className="alert alert-warn" role="alert">
          {t('rotate.changed')}
        </p>
      )}
      {resumed && keys.length > 0 && (
        <p className="alert alert-info" role="status">
          {t('rotate.resumed')}
        </p>
      )}
      {resumeFailure && (
        <p className="alert alert-error" role="alert">
          {failureText(resumeFailure)}
        </p>
      )}
      {data && keys.length === 0 && (
        <Empty title={t('rotate.none')} icon="lock">
          <Link to={SECRET_KEYS_PATH}>{t('rotate.back')}</Link>
        </Empty>
      )}
      {keys.length > 0 && (
        <section className="card">
          <SecretKeyTable keys={keys} functionsMark="redeploy" />
          {keys.some((k) => k.functions) && <p className="small muted">{t('rotate.functionsNote')}</p>}
          {unverified && <EmailUnverifiedNotice />}
          {error && (
            <p className="alert alert-error" role="alert">
              {error}
            </p>
          )}
          <div className="row-actions">
            <Link to={SECRET_KEYS_PATH} className="btn btn-ghost">
              {t('common.cancel')}
            </Link>
            <button type="button" className="btn btn-danger" disabled={busy || preview.loading} onClick={() => void rotate()}>
              {busy ? t('rotate.busy') : t('rotate.confirm', { count: keys.length })}
            </button>
          </div>
        </section>
      )}
    </Frame>
  );
}

/** 作り直しの結果。新しい値はここで 1 度だけ（冒頭の WHY） */
function RotateDone({ result }: { result: RotateResult }) {
  const { t } = useT();
  const navigate = useNavigate();
  // タブを閉じる・再読み込みで値を失う前に一度止める（ブラウザの既定の確認）
  useEffect(() => {
    if (!result.created.length) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [result.created.length]);

  // 置き換えた古い鍵の頭（「sk_live_ab12… の代わり」）。値は無い（displayPrefix だけ）
  const oldHead = new Map(result.revoked.map((k) => [k.id, keyHead(k.displayPrefix)]));
  // Functions の再デプロイは「プロジェクト × 環境」で 1 行（同じ所に鍵が 2 本あっても 1 回のデプロイで足りる）
  const redeploy = result.functionsRedeployRequired.filter(
    (f, i, all) => all.findIndex((x) => x.projectId === f.projectId && x.environment === f.environment) === i
  );

  return (
    <Frame>
      <PageHeader eyebrow={t('skList.title')} title={t('rotate.doneTitle')} />
      <p className="muted">{t('rotate.doneRevoked', { count: result.revoked.length })}</p>
      {result.created.length > 0 && (
        <section className="card reveal-card">
          <div className="alert alert-warn" role="note">
            <strong>{t('rotate.doneWarn')}</strong>
          </div>
          {groupByProject(result.created).map((g) => (
            <div key={g.projectId} className="sk-group">
              <h3 className="section-title sk-group-title">
                <span className="sk-text">{g.projectName || g.projectId.slice(0, 8)}</span>
              </h3>
              <div className="reveal-keys">
                {g.items.map((k) => (
                  <div key={k.id} className="keyline keyline-secret">
                    <div className="keyline-meta">
                      <span className="pill pill-danger">{t('key.kind.secret')}</span>
                      <span className="keyline-env">{t(`env.inline.${k.environment}`)}</span>
                      <span className="muted sk-text">{k.label ?? t('sk.noLabel')}</span>
                      {oldHead.get(k.replaces) && <span className="muted small">{t('rotate.replaces', { key: oldHead.get(k.replaces)! })}</span>}
                    </div>
                    <div className="keyline-value">
                      <code>{k.token}</code>
                      <CopyButton text={k.token} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
      {redeploy.length > 0 && (
        <section className="card">
          <h2 className="card-title">{t('rotate.redeployTitle')}</h2>
          <p>{t('rotate.redeployBody')}</p>
          <div className="stack">
            {redeploy.map((f) => (
              <CodeBlock key={`${f.projectId}:${f.environment}`} label={`${f.projectName || f.projectId.slice(0, 8)} · ${t(`env.inline.${f.environment}`)}`} code={functionsDeployCommand(CLI, f.environment)} />
            ))}
          </div>
        </section>
      )}
      <div className="row-actions">
        <button type="button" className="btn btn-primary" onClick={() => navigate(SECRET_KEYS_PATH, { replace: true })}>
          {t('rotate.finish')}
        </button>
      </div>
    </Frame>
  );
}
